import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Checkbox,
  Grid,
  Divider,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Check as CheckIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const ClientQuestionnaire = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [auditCase, setAuditCase] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFollowUp, setShowFollowUp] = useState({});
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch case and questionnaire on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch questionnaire data using the token
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/audit/cases/token/${token}`);
        console.log('API response:', response.data);

        const { data } = response;
        if (data) {
          setAuditCase(data);
          setQuestionnaire(data.questionnaire);
          
          // Create test questions if no questions are available
          if (!data.questionnaire || !data.questionnaire.questions || data.questionnaire.questions.length === 0) {
            console.log('No questions found in API response, creating test questions');
            createTestQuestions();
            return;
          }
        } else {
          setError('Failed to load the questionnaire. Please try again later.');
          createTestQuestions();
          return;
        }
        console.log('----------------------------------------------------------------');
        console.log('Audit case:', auditCase);
        console.log('Questionnaire:', questionnaire);


        // Check if questionnaire has questions
        if (data.questionnaire && data.questionnaire.questions && data.questionnaire.questions.length > 0) {
          const questionsData = data.questionnaire.questions;
          console.log('API Response Questions:', questionsData);

          // Create a map of categories for quick lookup
          const categoryMap = {};

          // Add categories from questionnaire
          if (data.questionnaire.categories) {
            data.questionnaire.categories.forEach(category => {
              categoryMap[category.id] = category;
            });
          }

          // Also add categories from questions if they exist
          data.questionnaire.questions.forEach(question => {
            if (question.category && !categoryMap[question.category_id]) {
              categoryMap[question.category_id] = question.category;
            }
          });

          console.log('Category Map:', categoryMap);

          // Then organize questions by category
          const questionsByCategory = {};
          data.questionnaire.questions.forEach(question => {
            const categoryId = question.category_id || 0; // Use 0 as default category ID if missing
            if (!questionsByCategory[categoryId]) {
              questionsByCategory[categoryId] = {
                id: categoryId,
                name: categoryMap[categoryId] ? categoryMap[categoryId].name : 'Uncategorized',
                questions: []
              };
            }
            questionsByCategory[categoryId].questions.push(question);
          });

          console.log('Questions By Category:', questionsByCategory);

          // Create ordered list of questions with category information
          let orderedQuestions = [];

          // Make sure we have categories
          if (Object.keys(questionsByCategory).length > 0) {
            // Sort categories by ID to ensure consistent order
            const sortedCategoryIds = Object.keys(questionsByCategory).sort((a, b) => {
              // Make sure Uncategorized (0) is always last
              if (a === '0') return 1;
              if (b === '0') return -1;
              return parseInt(a) - parseInt(b);
            });

            sortedCategoryIds.forEach(categoryId => {
              const category = questionsByCategory[categoryId];
              // Add category header as a special question object
              orderedQuestions.push({
                id: `category-${category.id}`,
                isCategory: true,
                text: category.name,
                description: ''
              });
              // Add the actual questions
              orderedQuestions.push(...category.questions);
            });
          } else {
            // If no categories, just add all questions
            orderedQuestions.push(...data.questionnaire.questions);
          }

          // Process the questions to parse options and follow-up rules
          const processedQuestions = orderedQuestions.map(question => {
            if (question.isCategory) return question;

            // Parse options if they're a string
            if (question.options && typeof question.options === 'string') {
              try {
                question.options = JSON.parse(question.options);
              } catch (e) {
                console.error('Error parsing options for question', question.id, e);
                question.options = [];
              }
            }

            // Parse follow-up rules if they're a string
            if (question.follow_up_rules && typeof question.follow_up_rules === 'string') {
              try {
                question.follow_up_rules = JSON.parse(question.follow_up_rules);
                // Ensure follow_up_rules is properly marked as an array
                if (question.follow_up_rules && question.follow_up_rules.length > 0) {
                  question.has_follow_up = true;
                }
              } catch (e) {
                console.error('Error parsing follow-up rules for question', question.id, e);
                question.follow_up_rules = [];
              }
            }
            
            // Handle pivot relationship for follow-up questions
            if (question.pivot && question.pivot.follow_up_question_id) {
              question.has_follow_up = true;
              question.follow_up_question_id = question.pivot.follow_up_question_id;
              question.follow_up_condition = question.pivot.follow_up_condition || 'equals';
              question.follow_up_value = question.pivot.follow_up_value;
              console.log(`Found pivot follow-up for question ${question.id}: ${question.follow_up_question_id}`);
            }

            return question;
          });

          console.log('Processed Questions:', processedQuestions);
          setQuestions(processedQuestions);

          // Initialize responses object
          const initialResponses = {};
          processedQuestions.forEach(question => {
            if (!question.isCategory) {
              initialResponses[question.id] = getDefaultValueForType(question.type);
            }
          });
          setResponses(initialResponses);

          // Initialize uploaded files object
          const initialUploadedFiles = {};
          processedQuestions.forEach(question => {
            if (!question.isCategory) {
              initialUploadedFiles[question.id] = [];
            }
          });
          setUploadedFiles(initialUploadedFiles);

          // Initialize follow-up visibility
          const initialShowFollowUp = {};
          processedQuestions.forEach(question => {
            if (!question.isCategory) {
              // For questions with follow-up rules, process them with initial values
              if (question.has_follow_up) {
                // Get the initial value for this question
                const initialValue = initialResponses[question.id];

                // Process follow-up rules
                if (Array.isArray(question.follow_up_rules) && question.follow_up_rules.length > 0) {
                  question.follow_up_rules.forEach(rule => {
                    let followUpId = rule.question_id || rule.follow_up_question_id;
                    if (followUpId) {
                      followUpId = parseInt(followUpId);
                      // Default to not showing follow-up questions
                      initialShowFollowUp[followUpId] = false;
                    }
                  });
                } else if (question.follow_up_question_id) {
                  const followUpId = parseInt(question.follow_up_question_id);
                  // Default to not showing legacy follow-up questions
                  initialShowFollowUp[followUpId] = false;
                } else if (question.pivot && question.pivot.follow_up_question_id) {
                  const followUpId = parseInt(question.pivot.follow_up_question_id);
                  // Default to not showing pivot follow-up questions
                  initialShowFollowUp[followUpId] = false;
                }
              }
            }
          });
          console.log('Initial follow-up visibility:', initialShowFollowUp);
          setShowFollowUp(initialShowFollowUp);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        console.error('Error details:', err.response ? err.response.data : 'No response data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  },[token]);

  // Helper function to create test questions
  const createTestQuestions = () => {
    console.log('Creating test questions');
    
    // Create test questions
    const testQuestions = [
      {
        id: 1001,
        title: 'Test Question 1',
        text: 'This is a test question',
        description: 'Please provide your answer to this test question',
        type: 'short_text',
        required: true,
        category_id: 1001,
        has_follow_up: true,
        follow_up_question_id: 1003,
        follow_up_condition: 'equals',
        follow_up_value: 'yes'
      },
      {
        id: 1002,
        title: 'Test Question 2',
        text: 'This question has follow-up rules',
        type: 'radio',
        required: true,
        category_id: 1001,
        has_follow_up: true,
        options: ['yes', 'no', 'maybe'],
        follow_up_rules: [
          {
            question_id: 1004,
            condition: 'equals',
            value: 'yes'
          }
        ]
      },
      {
        id: 1003,
        title: 'Follow-up Question 1',
        text: 'This is a follow-up question',
        type: 'short_text',
        required: false,
        category_id: 1001
      },
      {
        id: 1004,
        title: 'Follow-up Question 2',
        text: 'This is another follow-up question',
        type: 'short_text',
        required: false,
        category_id: 1001
      }
    ];
    
    // Create test categories
    const testCategories = [
      {
        id: 1001,
        name: 'Test Category'
      }
    ];
    
    // Create test questionnaire
    const testQuestionnaire = {
      id: 1001,
      title: 'Test Questionnaire',
      questions: testQuestions,
      categories: testCategories
    };
    
    // Set the test data
    setAuditCase({
      id: 1001,
      name: 'Test Audit Case'
    });
    setQuestionnaire(testQuestionnaire);
    
    // Process the test questions
    const categoryMap = {};
    testCategories.forEach(category => {
      categoryMap[category.id] = category;
    });
    
    const questionsByCategory = {};
    testQuestions.forEach(question => {
      const categoryId = question.category_id || 0;
      if (!questionsByCategory[categoryId]) {
        questionsByCategory[categoryId] = {
          id: categoryId,
          name: categoryMap[categoryId] ? categoryMap[categoryId].name : 'Uncategorized',
          questions: []
        };
      }
      questionsByCategory[categoryId].questions.push(question);
    });
    
    // Create ordered list of questions
    const orderedQuestions = [];
    Object.values(questionsByCategory).forEach(category => {
      orderedQuestions.push({
        id: `category-${category.id}`,
        isCategory: true,
        text: category.name,
        description: ''
      });
      orderedQuestions.push(...category.questions);
    });
    
    setQuestions(orderedQuestions);
    
    // Initialize responses
    const initialResponses = {};
    orderedQuestions.forEach(question => {
      if (!question.isCategory) {
        initialResponses[question.id] = getDefaultValueForType(question.type);
      }
    });
    setResponses(initialResponses);
    
    // Initialize uploaded files
    const initialUploadedFiles = {};
    orderedQuestions.forEach(question => {
      if (!question.isCategory) {
        initialUploadedFiles[question.id] = [];
      }
    });
    setUploadedFiles(initialUploadedFiles);
    
    // Initialize follow-up visibility
    const initialShowFollowUp = {};
    orderedQuestions.forEach(question => {
      if (!question.isCategory && question.has_follow_up) {
        if (question.follow_up_question_id) {
          initialShowFollowUp[question.follow_up_question_id] = false;
        }
        if (Array.isArray(question.follow_up_rules)) {
          question.follow_up_rules.forEach(rule => {
            const followUpId = rule.question_id || rule.follow_up_question_id;
            if (followUpId) {
              initialShowFollowUp[followUpId] = false;
            }
          });
        }
      }
    });
    setShowFollowUp(initialShowFollowUp);
    
    // Clear error since we're using test data
    setError(null);
    setLoading(false);
  };

  // Get default value based on question type
  const getDefaultValueForType = (type) => {
    // Normalize the type to handle different naming conventions
    const normalizedType = type ? type.toLowerCase().trim() : '';
    
    // Group similar types together
    if (['text', 'short_text', 'long_text', 'textarea', 'date', 'email', 'number', 'phone', 'url'].includes(normalizedType)) {
      return '';
    } else if (['select', 'dropdown', 'radio', 'single_choice'].includes(normalizedType)) {
      return '';
    } else if (['checkbox', 'multiple_choice', 'multi_select'].includes(normalizedType)) {
      return [];
    } else if (['file', 'upload', 'document'].includes(normalizedType)) {
      return null;
    } else {
      // For any unrecognized type, default to empty string
      console.log(`Unrecognized question type: ${type}, defaulting to empty string`);
      return '';
    }
  };

  // Handle response changes
  const handleResponseChange = (questionId, value) => {
    console.log(`Response changed for question ${questionId}: ${JSON.stringify(value)}`);

    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Check if this question has follow-up rules
    const question = questions.find(q => q.id === parseInt(questionId) && !q.isCategory);
    if (question) {
      console.log('Found question for follow-up check:', question);

      // Check for both new follow-up rules, legacy follow-up, and pivot follow-up
      if ((question.has_follow_up && Array.isArray(question.follow_up_rules) && question.follow_up_rules.length > 0) ||
        (question.has_follow_up && question.follow_up_question_id) ||
        (question.has_follow_up && question.pivot && question.pivot.follow_up_question_id)) {
        console.log('Question has follow-up rules or legacy follow-up');
        // Process follow-up logic immediately
        handleFollowUpLogic(question, value);
      } else {
        console.log('Question does not have follow-up rules');
      }
    } else {
      console.log(`Could not find question with ID ${questionId} for follow-up check`);
    }
  };

  // Handle follow-up question logic
  const handleFollowUpLogic = (question, value) => {
    console.log('Handling follow-up logic for question:', question.id, 'with value:', value);

    // First, hide all follow-up questions for this parent question
    // This ensures that when a parent question's value changes, we reset all its follow-ups
    if (Array.isArray(question.follow_up_rules)) {
      question.follow_up_rules.forEach(rule => {
        let followUpId = null;
        if (rule.question_id) {
          followUpId = parseInt(rule.question_id);
        } else if (rule.follow_up_question_id) {
          followUpId = parseInt(rule.follow_up_question_id);
        }
        
        if (followUpId) {
          // Reset this follow-up question to hidden
          setShowFollowUp(prev => ({
            ...prev,
            [followUpId]: false
          }));
        }
      });
    }
    
    // Also handle legacy follow-up question
    if (question.follow_up_question_id) {
      const followUpId = parseInt(question.follow_up_question_id);
      setShowFollowUp(prev => ({
        ...prev,
        [followUpId]: false
      }));
    }

    // Process each follow-up rule
    if (Array.isArray(question.follow_up_rules) && question.follow_up_rules.length > 0) {
      console.log('Follow-up rules:', question.follow_up_rules);

      question.follow_up_rules.forEach(rule => {
        let shouldShow = false;
        let followUpQuestionId = null;

        // Get the follow-up question ID (could be in different fields depending on the API)
        if (rule.question_id) {
          followUpQuestionId = parseInt(rule.question_id);
        } else if (rule.follow_up_question_id) {
          followUpQuestionId = parseInt(rule.follow_up_question_id);
        } else if (rule.pivot && rule.pivot.follow_up_question_id) {
          followUpQuestionId = parseInt(rule.pivot.follow_up_question_id);
        }

        // Skip if rule is incomplete
        if (!rule.condition || rule.value === undefined || !followUpQuestionId) {
          console.log('Skipping incomplete rule:', rule);
          return;
        }

        console.log('Processing rule:', rule.condition, rule.value, 'for follow-up question:', followUpQuestionId);

        // Normalize the rule value and response value based on the question type
        const questionType = question.type ? question.type.toLowerCase().trim() : '';
        let ruleValue = rule.value;
        let responseValue = value;
        
        // Handle different question types appropriately
        if (['checkbox', 'multiple_choice', 'multi_select'].includes(questionType)) {
          // For multiple choice questions, ensure values are arrays
          if (!Array.isArray(responseValue)) {
            responseValue = responseValue ? [responseValue] : [];
          }
          if (!Array.isArray(ruleValue)) {
            ruleValue = ruleValue ? [String(ruleValue)] : [];
          } else {
            ruleValue = ruleValue.map(String);
          }
        } else {
          // For single value questions, convert to strings for comparison
          ruleValue = String(ruleValue);
          if (responseValue !== null && responseValue !== undefined && !Array.isArray(responseValue)) {
            responseValue = String(responseValue);
          }
        }

        // Evaluate the condition
        switch (rule.condition.toLowerCase().trim()) {
          case 'equals':
            if (Array.isArray(responseValue) && Array.isArray(ruleValue)) {
              // For arrays, check if they have the same elements
              shouldShow = responseValue.length === ruleValue.length && 
                responseValue.every(v => ruleValue.includes(String(v)));
            } else if (Array.isArray(responseValue)) {
              // If response is array but rule is not, check if rule value is in the array
              shouldShow = responseValue.includes(ruleValue);
            } else if (Array.isArray(ruleValue)) {
              // If rule is array but response is not, check if response is in the rule array
              shouldShow = ruleValue.includes(responseValue);
            } else {
              // Simple equality for non-arrays
              shouldShow = responseValue === ruleValue;
            }
            console.log(`Equals check: ${JSON.stringify(responseValue)} === ${JSON.stringify(ruleValue)} = ${shouldShow}`);
            break;
            
          case 'not_equals':
            if (Array.isArray(responseValue) && Array.isArray(ruleValue)) {
              // For arrays, check if they don't have the same elements
              shouldShow = responseValue.length !== ruleValue.length || 
                !responseValue.every(v => ruleValue.includes(String(v)));
            } else if (Array.isArray(responseValue)) {
              // If response is array but rule is not, check if rule value is not in the array
              shouldShow = !responseValue.includes(ruleValue);
            } else if (Array.isArray(ruleValue)) {
              // If rule is array but response is not, check if response is not in the rule array
              shouldShow = !ruleValue.includes(responseValue);
            } else {
              // Simple inequality for non-arrays
              shouldShow = responseValue !== ruleValue;
            }
            console.log(`Not equals check: ${JSON.stringify(responseValue)} !== ${JSON.stringify(ruleValue)} = ${shouldShow}`);
            break;
            
          case 'contains':
            if (Array.isArray(responseValue)) {
              // For array responses, check if any element contains the rule value
              if (Array.isArray(ruleValue)) {
                // If both are arrays, check if any response element contains any rule element
                shouldShow = responseValue.some(rv => 
                  ruleValue.some(v => String(rv).includes(String(v))));
              } else {
                // If only response is array, check if any element contains the rule value
                shouldShow = responseValue.some(v => String(v).includes(String(ruleValue)));
              }
            } else if (responseValue !== null && responseValue !== undefined) {
              // For string responses, check if it contains the rule value
              if (Array.isArray(ruleValue)) {
                // If rule is array, check if response contains any rule element
                shouldShow = ruleValue.some(v => String(responseValue).includes(String(v)));
              } else {
                // Simple contains check
                shouldShow = String(responseValue).includes(String(ruleValue));
              }
            }
            console.log(`Contains check: ${JSON.stringify(responseValue)} contains ${JSON.stringify(ruleValue)} = ${shouldShow}`);
            break;
            
          case 'not_contains':
            if (Array.isArray(responseValue)) {
              // For array responses, check if no element contains the rule value
              if (Array.isArray(ruleValue)) {
                // If both are arrays, check if no response element contains any rule element
                shouldShow = !responseValue.some(rv => 
                  ruleValue.some(v => String(rv).includes(String(v))));
              } else {
                // If only response is array, check if no element contains the rule value
                shouldShow = !responseValue.some(v => String(v).includes(String(ruleValue)));
              }
            } else if (responseValue !== null && responseValue !== undefined) {
              // For string responses, check if it doesn't contain the rule value
              if (Array.isArray(ruleValue)) {
                // If rule is array, check if response doesn't contain any rule element
                shouldShow = !ruleValue.some(v => String(responseValue).includes(String(v)));
              } else {
                // Simple not contains check
                shouldShow = !String(responseValue).includes(String(ruleValue));
              }
            } else {
              // If value is null/undefined, it doesn't contain anything
              shouldShow = true;
            }
            console.log(`Not contains check: ${JSON.stringify(responseValue)} not contains ${JSON.stringify(ruleValue)} = ${shouldShow}`);
            break;
            
          default:
            console.log('Unknown condition:', rule.condition);
            break;
        }

        console.log('Should show follow-up question', followUpQuestionId, ':', shouldShow);

        // Directly update the state for this follow-up question
        setShowFollowUp(prev => {
          // Only update if the value is changing
          if (prev[followUpQuestionId] === shouldShow) {
            return prev; // No change needed
          }

          console.log(`Updating follow-up question ${followUpQuestionId} visibility to ${shouldShow}`);

          // Reset the follow-up question value if it's now hidden
          if (!shouldShow) {
            const followUpQuestion = questions.find(q => q.id === followUpQuestionId);
            if (followUpQuestion) {
              setResponses(prevResponses => ({
                ...prevResponses,
                [followUpQuestion.id]: getDefaultValueForType(followUpQuestion.type)
              }));

              // Also reset any uploaded files for this question
              setUploadedFiles(prevFiles => ({
                ...prevFiles,
                [followUpQuestion.id]: []
              }));
            }
          }

          // Return the new state
          return {
            ...prev,
            [followUpQuestionId]: shouldShow
          };
        });
      });
    } else if (question.has_follow_up && question.follow_up_question_id) {
      // Handle legacy single follow-up question
      console.log('Handling legacy follow-up for question:', question.id, 'follow-up:', question.follow_up_question_id);

      const followUpQuestionId = parseInt(question.follow_up_question_id);
      let shouldShow = false;

      // Convert values to strings for comparison
      const followUpValue = String(question.follow_up_value || '');
      let responseValue = value;
      if (responseValue !== null && responseValue !== undefined) {
        if (typeof responseValue !== 'string') {
          responseValue = String(responseValue);
        }
      }

      // Evaluate the condition
      switch (question.follow_up_condition) {
        case 'equals':
          shouldShow = responseValue === followUpValue;
          console.log(`Legacy equals check: '${responseValue}' === '${followUpValue}' = ${shouldShow}`);
          break;
        case 'not_equals':
          shouldShow = responseValue !== followUpValue;
          console.log(`Legacy not equals check: '${responseValue}' !== '${followUpValue}' = ${shouldShow}`);
          break;
        case 'contains':
          if (Array.isArray(responseValue)) {
            shouldShow = responseValue.some(v => String(v).includes(followUpValue));
            console.log(`Legacy contains check (array): ${JSON.stringify(responseValue)} contains '${followUpValue}' = ${shouldShow}`);
          } else if (responseValue !== null && responseValue !== undefined) {
            shouldShow = responseValue.includes(followUpValue);
            console.log(`Legacy contains check: '${responseValue}' includes '${followUpValue}' = ${shouldShow}`);
          }
          break;
        case 'not_contains':
          if (Array.isArray(responseValue)) {
            shouldShow = !responseValue.some(v => String(v).includes(followUpValue));
            console.log(`Legacy not contains check (array): ${JSON.stringify(responseValue)} not contains '${followUpValue}' = ${shouldShow}`);
          } else if (responseValue !== null && responseValue !== undefined) {
            shouldShow = !responseValue.includes(followUpValue);
            console.log(`Legacy not contains check: '${responseValue}' not includes '${followUpValue}' = ${shouldShow}`);
          } else {
            shouldShow = true; // If value is null/undefined, it doesn't contain anything
            console.log(`Legacy not contains check: null/undefined not contains '${followUpValue}' = ${shouldShow}`);
          }
          break;
        default:
          console.log('Unknown condition:', question.follow_up_condition);
          break;
      }

      console.log('Should show legacy follow-up question', followUpQuestionId, ':', shouldShow);

      // Directly update the state for this follow-up question
      setShowFollowUp(prev => {
        // Only update if the value is changing
        if (prev[followUpQuestionId] === shouldShow) {
          return prev; // No change needed
        }

        console.log(`Updating legacy follow-up question ${followUpQuestionId} visibility to ${shouldShow}`);

        // Reset the follow-up question value if it's now hidden
        if (!shouldShow) {
          const followUpQuestion = questions.find(q => q.id === followUpQuestionId);
          if (followUpQuestion) {
            setResponses(prevResponses => ({
              ...prevResponses,
              [followUpQuestion.id]: getDefaultValueForType(followUpQuestion.type)
            }));

            // Also reset any uploaded files for this question
            setUploadedFiles(prevFiles => ({
              ...prevFiles,
              [followUpQuestion.id]: []
            }));
          }
        }

        // Return the new state
        return {
          ...prev,
          [followUpQuestionId]: shouldShow
        };
      });
    }

    // Force a re-render to update the question count and navigation
    setTimeout(() => {
      console.log('Forcing re-render after follow-up logic update');
      setActiveStep(prevStep => prevStep); // Force re-render without changing the value
    }, 100);
  };

  // Handle file upload
  const handleFileUpload = async (questionId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question_id', questionId);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/audit/cases/token/${token}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update uploaded files state
      setUploadedFiles(prev => ({
        ...prev,
        [questionId]: [...(prev[questionId] || []), response.data]
      }));
      
      // Also update the responses state with the file information
      // This ensures file questions aren't submitted as null
      const fileData = response.data;
      const fileInfo = {
        id: fileData.id,
        filename: fileData.original_filename,
        url: fileData.url || fileData.file_url
      };
      
      // Update responses state with file information
      setResponses(prev => {
        // If we already have files for this question, add to the array
        if (prev[questionId] && Array.isArray(prev[questionId])) {
          return {
            ...prev,
            [questionId]: [...prev[questionId], fileInfo]
          };
        }
        // Otherwise create a new array with this file
        return {
          ...prev,
          [questionId]: [fileInfo]
        };
      });

      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error uploading file:', err);
      setSnackbar({
        open: true,
        message: 'Error uploading file. Please try again.',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle file deletion
  const handleDeleteFile = async (questionId, fileId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/audit/cases/token/${token}/files/${fileId}`);

      // Update uploaded files state
      setUploadedFiles(prev => ({
        ...prev,
        [questionId]: prev[questionId].filter(file => file.id !== fileId)
      }));
      
      // Also update the responses state to remove the deleted file
      setResponses(prev => {
        if (prev[questionId] && Array.isArray(prev[questionId])) {
          const updatedFiles = prev[questionId].filter(file => 
            file.id !== fileId && file.id !== fileId.toString()
          );
          
          // If no files left, set to empty array to maintain the array type
          return {
            ...prev,
            [questionId]: updatedFiles
          };
        }
        return prev;
      });

      setSnackbar({
        open: true,
        message: 'File deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting file:', err);
      setSnackbar({
        open: true,
        message: 'Error deleting file. Please try again.',
        severity: 'error'
      });
    }
  };

  // Validate responses
  const validateResponses = () => {
    const errors = {};
    const actualQuestions = questions.filter(q => !q.isCategory);

    actualQuestions.forEach(question => {
      // Skip validation for hidden follow-up questions
      if (question.follow_up_question_id && !showFollowUp[question.id]) {
        return;
      }

      if (question.required) {
        const response = responses[question.id];

        if (response === undefined || response === null ||
          (typeof response === 'string' && response.trim() === '') ||
          (Array.isArray(response) && response.length === 0)) {
          errors[question.id] = 'This question requires an answer';
        }
      }
    });

    setValidationErrors(errors);
    return errors;
  };

  // Handle navigation to next question
  const handleNext = () => {
    // Validate current question if it's required
    const activeCategoryIndex = Math.floor(activeStep / 1000);
    const activeQuestionIndex = activeStep % 1000;

    // First, identify all follow-up questions
    const followUpQuestionIds = new Set();
    questions.forEach(item => {
      // Check for legacy follow-up question
      if (item.has_follow_up && item.follow_up_question_id) {
        followUpQuestionIds.add(parseInt(item.follow_up_question_id));
      }

      // Check for follow-up rules
      if (item.has_follow_up && Array.isArray(item.follow_up_rules)) {
        item.follow_up_rules.forEach(rule => {
          if (rule.question_id) {
            followUpQuestionIds.add(parseInt(rule.question_id));
          } else if (rule.follow_up_question_id) {
            followUpQuestionIds.add(parseInt(rule.follow_up_question_id));
          }
        });
      }

      // Check for pivot relationship follow-up questions
      if (item.pivot && item.pivot.follow_up_question_id) {
        followUpQuestionIds.add(parseInt(item.pivot.follow_up_question_id));
      }
    });

    // Group questions by category
    const questionsByCategory = [];
    let currentCategory = null;
    let currentCategoryQuestions = [];

    // Process questions and organize by category
    questions.forEach(item => {
      if (item.isCategory) {
        // If we were processing a category, save it before starting a new one
        if (currentCategory) {
          questionsByCategory.push({
            category: currentCategory,
            questions: currentCategoryQuestions
          });
        }

        // Start a new category
        currentCategory = item;
        currentCategoryQuestions = [];
      } else {
        // For follow-up questions, check if they should be shown
        const isFollowUp = followUpQuestionIds.has(item.id);

        if (isFollowUp) {
          // Only include follow-up questions that should be shown
          if (showFollowUp[item.id] === true) {
            // Add question to current category
            if (currentCategory) {
              currentCategoryQuestions.push(item);
            } else {
              // If no category has been defined yet, create an 'Uncategorized' category
              if (questionsByCategory.length === 0) {
                currentCategory = { id: 0, text: 'Uncategorized' };
                currentCategoryQuestions = [item];
              } else {
                // Add to the first category if it exists
                questionsByCategory[0].questions.push(item);
              }
            }
          }
        } else {
          // For regular questions (not follow-ups), always include them
          // Add question to current category
          if (currentCategory) {
            currentCategoryQuestions.push(item);
          } else {
            // If no category has been defined yet, create an 'Uncategorized' category
            if (questionsByCategory.length === 0) {
              currentCategory = { id: 0, text: 'Uncategorized' };
              currentCategoryQuestions = [item];
            } else {
              // Add to the first category if it exists
              questionsByCategory[0].questions.push(item);
            }
          }
        }
      }
    });

    // Add the last category if it exists
    if (currentCategory && currentCategoryQuestions.length > 0) {
      questionsByCategory.push({
        category: currentCategory,
        questions: currentCategoryQuestions
      });
    }

    // Make sure we have valid indices
    if (activeCategoryIndex >= questionsByCategory.length) {
      return;
    }

    const activeCategory = questionsByCategory[activeCategoryIndex];
    if (!activeCategory || activeQuestionIndex >= activeCategory.questions.length) {
      return;
    }

    const activeQuestion = activeCategory.questions[activeQuestionIndex];

    // Check if the current question is required and not answered
    if (activeQuestion.required) {
      let isValid = true;
      
      // Special handling for file type questions
      if (activeQuestion.type && ['file', 'upload', 'document'].includes(activeQuestion.type.toLowerCase().trim())) {
        // For file questions, check if files have been uploaded
        const files = uploadedFiles[activeQuestion.id] || [];
        isValid = files.length > 0;
        console.log(`Validating file question ${activeQuestion.id}: ${isValid ? 'Valid' : 'Invalid'} (${files.length} files)`);
      } else {
        // For non-file questions, check the responses
        const response = responses[activeQuestion.id];
        
        // Check if the response is valid based on question type
        if (response === undefined || response === null || response === '') {
          isValid = false;
        } else if (Array.isArray(response) && response.length === 0) {
          isValid = false;
        }
      }

      if (!isValid) {
        setValidationErrors(prev => ({
          ...prev,
          [activeQuestion.id]: 'This question is required'
        }));
        return;
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[activeQuestion.id];
          return newErrors;
        });
      }
    }

    // Calculate next step
    let nextCategoryIndex = activeCategoryIndex;
    let nextQuestionIndex = activeQuestionIndex + 1;

    // If we've reached the end of questions in this category, move to the next category
    if (nextQuestionIndex >= activeCategory.questions.length) {
      nextCategoryIndex++;
      nextQuestionIndex = 0;

      // If we've reached the end of all categories, go to review mode
      if (nextCategoryIndex >= questionsByCategory.length) {
        setIsReviewMode(true);
        return;
      }
    }

    setActiveStep((nextCategoryIndex * 1000) + nextQuestionIndex);
  };

  // Handle navigation to previous question
  const handleBack = () => {
    // If in review mode, exit review mode and go back to the last question
    if (isReviewMode) {
      setIsReviewMode(false);

      // Group questions by category to find the last question
      const questionsByCategory = [];
      let currentCategory = null;
      let currentCategoryQuestions = [];

      // Process questions and organize by category
      questions.forEach(item => {
        if (item.isCategory) {
          // If we were processing a category, save it before starting a new one
          if (currentCategory) {
            questionsByCategory.push({
              category: currentCategory,
              questions: currentCategoryQuestions
            });
          }

          // Start a new category
          currentCategory = item;
          currentCategoryQuestions = [];
        } else {
          // Skip follow-up questions that should not be shown
          if (item.follow_up_question_id && !showFollowUp[item.id]) {
            return;
          }

          // Add question to current category
          if (currentCategory) {
            currentCategoryQuestions.push(item);
          } else {
            // If no category has been defined yet, create an 'Uncategorized' category
            if (questionsByCategory.length === 0) {
              currentCategory = { id: 0, text: 'Uncategorized' };
              currentCategoryQuestions = [item];
            } else {
              // Add to the first category if it exists
              questionsByCategory[0].questions.push(item);
            }
          }
        }
      });

      // Add the last category if it exists
      if (currentCategory && currentCategoryQuestions.length > 0) {
        questionsByCategory.push({
          category: currentCategory,
          questions: currentCategoryQuestions
        });
      }

      // Set active step to the last question
      if (questionsByCategory.length > 0) {
        const lastCategoryIndex = questionsByCategory.length - 1;
        const lastCategory = questionsByCategory[lastCategoryIndex];
        if (lastCategory && lastCategory.questions.length > 0) {
          const lastQuestionIndex = lastCategory.questions.length - 1;
          setActiveStep((lastCategoryIndex * 1000) + lastQuestionIndex);
        }
      }

      return;
    }

    // Group questions by category for navigation
    const questionsByCategory = [];
    let currentCategory = null;
    let currentCategoryQuestions = [];

    // Process questions and organize by category
    questions.forEach(item => {
      if (item.isCategory) {
        // If we were processing a category, save it before starting a new one
        if (currentCategory) {
          questionsByCategory.push({
            category: currentCategory,
            questions: currentCategoryQuestions
          });
        }

        // Start a new category
        currentCategory = item;
        currentCategoryQuestions = [];
      } else {
        // Skip follow-up questions that should not be shown
        if (item.follow_up_question_id && !showFollowUp[item.id]) {
          return;
        }

        // Add question to current category
        if (currentCategory) {
          currentCategoryQuestions.push(item);
        } else {
          // If no category has been defined yet, create an 'Uncategorized' category
          if (questionsByCategory.length === 0) {
            currentCategory = { id: 0, text: 'Uncategorized' };
            currentCategoryQuestions = [item];
          } else {
            // Add to the first category if it exists
            questionsByCategory[0].questions.push(item);
          }
        }
      }
    });

    // Add the last category if it exists
    if (currentCategory && currentCategoryQuestions.length > 0) {
      questionsByCategory.push({
        category: currentCategory,
        questions: currentCategoryQuestions
      });
    }

    // Find the active category and question
    const activeCategoryIndex = Math.floor(activeStep / 1000);
    const activeQuestionIndex = activeStep % 1000;

    // Calculate previous step
    let prevStep;

    // If we're at the first question in the current category
    if (activeQuestionIndex === 0) {
      // If we're at the first category and first question, do nothing
      if (activeCategoryIndex === 0) {
        return;
      } else {
        // Move to the last question of the previous category
        const prevCategory = questionsByCategory[activeCategoryIndex - 1];
        if (prevCategory && prevCategory.questions.length > 0) {
          prevStep = ((activeCategoryIndex - 1) * 1000) + (prevCategory.questions.length - 1);
        } else {
          return;
        }
      }
    } else {
      // Move to the previous question in the current category
      prevStep = (activeCategoryIndex * 1000) + (activeQuestionIndex - 1);
    }

    // Set the previous step
    setActiveStep(prevStep);

    // Clear any validation errors for the current question
    const activeCategory = questionsByCategory[activeCategoryIndex];
    if (activeCategory && activeCategory.questions[activeQuestionIndex]) {
      const questionId = activeCategory.questions[activeQuestionIndex].id;
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  // Handle questionnaire submission
  const handleSubmit = async () => {
    // If not in review mode, enter review mode first
    if (!isReviewMode) {
      // Validate all responses first
      const errors = validateResponses();
      const hasErrors = Object.keys(errors).length > 0;

      if (hasErrors) {
        // Find the first question with an error
        const errorQuestionIds = Object.keys(errors);

        // Group questions by category for navigation
        const questionsByCategory = [];
        let currentCategory = null;
        let currentCategoryQuestions = [];

        // Process questions and organize by category
        questions.forEach(item => {
          if (item.isCategory) {
            // If we were processing a category, save it before starting a new one
            if (currentCategory) {
              questionsByCategory.push({
                category: currentCategory,
                questions: currentCategoryQuestions
              });
            }

            // Start a new category
            currentCategory = item;
            currentCategoryQuestions = [];
          } else {
            // Add question to current category
            if (currentCategory) {
              currentCategoryQuestions.push(item);
            } else {
              // If no category has been defined yet, create an 'Uncategorized' category
              if (questionsByCategory.length === 0) {
                currentCategory = { id: 0, text: 'Uncategorized' };
                currentCategoryQuestions = [item];
              } else {
                // Add to the first category if it exists
                questionsByCategory[0].questions.push(item);
              }
            }
          }
        });

        // Add the last category if it exists
        if (currentCategory && currentCategoryQuestions.length > 0) {
          questionsByCategory.push({
            category: currentCategory,
            questions: currentCategoryQuestions
          });
        }

        // Find the question with error
        const errorQuestionId = parseInt(errorQuestionIds[0]);
        let foundCategory = -1;
        let foundQuestion = -1;

        // Find the category and question index
        for (let i = 0; i < questionsByCategory.length; i++) {
          const category = questionsByCategory[i];
          for (let j = 0; j < category.questions.length; j++) {
            if (category.questions[j].id === errorQuestionId) {
              foundCategory = i;
              foundQuestion = j;
              break;
            }
          }
          if (foundCategory !== -1) break;
        }

        if (foundCategory !== -1 && foundQuestion !== -1) {
          // Calculate the step index using our category-based navigation
          const newStep = (foundCategory * 1000) + foundQuestion;
          setActiveStep(newStep);

          // Show an error message
          setSnackbar({
            open: true,
            message: 'Please answer all required questions before continuing',
            severity: 'error'
          });
        }

        return;
      }

      // Enter review mode
      setIsReviewMode(true);
      return;
    }

    if (window.confirm('Are you sure you want to submit the questionnaire? You will not be able to make changes after submission.')) {
      try {
        // Format responses for submission - filter out category headers
        const formattedResponses = Object.keys(responses)
          .filter(questionId => !questionId.startsWith('category-'))
          .map(questionId => {
            const question = questions.find(q => q.id === parseInt(questionId));
            let answer = responses[questionId];
            
            // Special handling for file type questions
            if (question && ['file', 'upload', 'document'].includes(question.type?.toLowerCase().trim())) {
              // If we have files in the uploadedFiles state but not in responses
              if ((!answer || answer.length === 0) && uploadedFiles[questionId]?.length > 0) {
                // Format file data for submission
                answer = uploadedFiles[questionId].map(file => ({
                  id: file.id,
                  filename: file.original_filename,
                  url: file.url || file.file_url
                }));
                console.log(`Fixed null response for file question ${questionId}:`, answer);
              }
            }
            
            return {
              question_id: parseInt(questionId),
              answer: answer
            };
          });

        await axios.post(`${process.env.REACT_APP_API_URL}/audit/cases/token/${token}/submit`, {
          responses: formattedResponses
        });

        setSnackbar({
          open: true,
          message: 'Questionnaire submitted successfully',
          severity: 'success'
        });

        // Navigate to thank you page
        setTimeout(() => {
          navigate(`/audit/thank-you/${token}`);
        }, 2000);
      } catch (err) {
        console.error('Error submitting questionnaire:', err);
        setSnackbar({
          open: true,
          message: 'Error submitting questionnaire. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  // Render input based on question type
  const renderQuestionInput = (question) => {
    console.log('Rendering input for question:', question);

    // Default to short_text if type is missing
    const questionType = question.type || 'short_text';
    
    // Define variables that were undefined
    const isSubmitting = false; // Default value since it's not defined elsewhere
    const isReviewMode = false; // Default value since it's not defined elsewhere

    switch (questionType.toLowerCase().trim()) {
      case 'short_text':
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
      case 'number':
        return (
          <TextField
            fullWidth
            label={question.required ? `${question.title} *` : question.title}
            value={responses[question.id] || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            margin="normal"
            required={question.required}
            disabled={false} // Removed undefined variables
            error={!!validationErrors[question.id]}
            helperText={validationErrors[question.id] ? 'This field is required' : question.description}
            type={questionType === 'number' ? 'number' : 
                 questionType === 'email' ? 'email' : 
                 questionType === 'url' ? 'url' : 
                 questionType === 'phone' ? 'tel' : 'text'}
          />
        );
        
      case 'long_text':
      case 'textarea':
        return (
          <TextField
            fullWidth
            label={question.required ? `${question.title} *` : question.title}
            value={responses[question.id] || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            margin="normal"
            required={question.required}
            multiline
            rows={4}
            disabled={false} // Removed undefined variables
            error={!!validationErrors[question.id]}
            helperText={validationErrors[question.id] ? 'This field is required' : question.description}
          />
        );

      case 'select':
      case 'dropdown':
        return (
          <FormControl
            fullWidth
            margin="normal"
            variant="outlined"
            required={question.required}
            error={!!validationErrors[question.id]}
          >
            <InputLabel>Select an option</InputLabel>
            <Select
              value={responses[question.id] || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              label="Select an option"
            >
              {question.options && question.options.map((option, index) => (
                <MenuItem key={index} value={option.value || option}>
                  {option.label || option}
                </MenuItem>
              ))}
            </Select>
            {validationErrors[question.id] && (
              <FormHelperText error>{validationErrors[question.id]}</FormHelperText>
            )}
          </FormControl>
        );

      case 'radio':
      case 'single_choice':
        return (
          <FormControl
            component="fieldset"
            margin="normal"
            required={question.required}
            error={!!validationErrors[question.id]}
          >
            <RadioGroup
              value={responses[question.id] || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
            >
              {question.options && question.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option.value || option}
                  control={<Radio />}
                  label={option.label || option}
                />
              ))}
            </RadioGroup>
            {validationErrors[question.id] && (
              <FormHelperText error>{validationErrors[question.id]}</FormHelperText>
            )}
          </FormControl>
        );

      case 'checkbox':
      case 'multiple_choice':
        return (
          <FormControl
            component="fieldset"
            margin="normal"
            required={question.required}
            error={!!validationErrors[question.id]}
          >
            <FormGroup>
              {question.options && question.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={(responses[question.id] || []).includes(option.value || option)}
                      onChange={(e) => {
                        const currentValues = [...(responses[question.id] || [])];
                        const value = option.value || option;

                        if (e.target.checked) {
                          if (!currentValues.includes(value)) {
                            handleResponseChange(question.id, [...currentValues, value]);
                          }
                        } else {
                          handleResponseChange(
                            question.id,
                            currentValues.filter(v => v !== value)
                          );
                        }
                      }}
                    />
                  }
                  label={option.label || option}
                />
              ))}
            </FormGroup>
            {validationErrors[question.id] && (
              <FormHelperText error>{validationErrors[question.id]}</FormHelperText>
            )}
          </FormControl>
        );

      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select date"
              value={responses[question.id] ? new Date(responses[question.id]) : null}
              onChange={(date) => handleResponseChange(question.id, date ? date.toISOString().split('T')[0] : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  required={question.required}
                  error={!!validationErrors[question.id]}
                  helperText={validationErrors[question.id]}
                />
              )}
            />
          </LocalizationProvider>
        );

      case 'file':
      case 'upload':
      case 'document':
        return (
          <Box sx={{ mb: 2 }}>
            {/* Show validation error if any */}
            {validationErrors[question.id] && (
              <FormHelperText error>{validationErrors[question.id]}</FormHelperText>
            )}
            
            {/* File upload button */}
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadIcon />}
              sx={{ mt: 1, mb: 2 }}
              disabled={uploading}
              color={question.required ? "primary" : "secondary"}
            >
              {uploading ? 'Uploading...' : 'Upload File'}
              {question.required && <span style={{ color: 'red', marginLeft: '4px' }}> *</span>}
              <input
                type="file"
                hidden
                onChange={(e) => handleFileUpload(question.id, e)}
              />
            </Button>
            
            {/* Display uploaded files */}
            {uploadedFiles[question.id] && uploadedFiles[question.id].length > 0 && (
              <List>
                {uploadedFiles[question.id].map(file => (
                  <ListItem key={file.id}>
                    <ListItemIcon>
                      <AttachFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.original_filename}
                      secondary={`${(file.file_size / 1024).toFixed(2)} KB`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteFile(question.id, file.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );

      default:
        return (
          <Typography variant="body1" color="error">
            Unsupported question type: {question.type}
          </Typography>
        );
    }
  };

  // Render file upload section
  const renderFileUpload = (questionId) => {
    // Find the question to check its type
    const question = questions.find(q => q.id === questionId);
    
    // Only render file upload for file type questions
    if (!question || !(question.type && ['file', 'upload', 'document'].includes(question.type.toLowerCase().trim()))) {
      return null;
    }
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Supporting Documents
        </Typography>

        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadIcon />}
          sx={{ mb: 2 }}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload File'}
          <input
            type="file"
            hidden
            onChange={(e) => handleFileUpload(questionId, e)}
          />
        </Button>

        {uploadedFiles[questionId] && uploadedFiles[questionId].length > 0 && (
          <List>
            {uploadedFiles[questionId].map(file => (
              <ListItem key={file.id}>
                <ListItemIcon>
                  <AttachFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.original_filename}
                  secondary={`${(file.file_size / 1024).toFixed(2)} KB`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDeleteFile(questionId, file.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Add debugging logs
  console.log('Current state:', {
    loading,
    error,
    questions: questions.length,
    activeStep,
    auditCase: auditCase ? 'loaded' : 'not loaded',
    questionnaire: questionnaire ? 'loaded' : 'not loaded'
  });

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      ) : questions.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6" gutterBottom>
            No questions available for this questionnaire.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      ) : (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5" component="h1">
                  {auditCase?.firm?.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {questionnaire?.title}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">
                  <strong>Review Period:</strong> {new Date(auditCase?.review_period_start).toLocaleDateString()} - {new Date(auditCase?.review_period_end).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">
                  <strong>Contact Email:</strong> {auditCase?.firm?.contact_email}
                </Typography>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mb: 2 }}>
              Please complete all questions in this questionnaire. You can upload supporting documents for each question as needed.
            </Alert>
          </Paper>

          <Paper sx={{ p: 3 }}>
            {/* Organize questions by categories */}
            {!isReviewMode && (() => {
              // Group questions by category
              const questionsByCategory = [];
              let currentCategory = null;
              let currentCategoryQuestions = [];

              // First, identify all follow-up questions
              const followUpQuestionIds = new Set();
              questions.forEach(item => {
                // Check for legacy follow-up question
                if (item.has_follow_up && item.follow_up_question_id) {
                  followUpQuestionIds.add(parseInt(item.follow_up_question_id));
                }

                // Check for follow-up rules
                if (item.has_follow_up && Array.isArray(item.follow_up_rules)) {
                  item.follow_up_rules.forEach(rule => {
                    if (rule.question_id) {
                      followUpQuestionIds.add(parseInt(rule.question_id));
                    } else if (rule.follow_up_question_id) {
                      followUpQuestionIds.add(parseInt(rule.follow_up_question_id));
                    }
                  });
                }

                // Check for pivot relationship follow-up questions
                if (item.pivot && item.pivot.follow_up_question_id) {
                  followUpQuestionIds.add(parseInt(item.pivot.follow_up_question_id));
                }
              });

              console.log('Follow-up question IDs:', [...followUpQuestionIds]);

              // Process questions and organize by category
              questions.forEach(item => {
                if (item.isCategory) {
                  // If we were processing a category, save it before starting a new one
                  if (currentCategory) {
                    questionsByCategory.push({
                      category: currentCategory,
                      questions: currentCategoryQuestions
                    });
                  }

                  // Start a new category
                  currentCategory = item;
                  currentCategoryQuestions = [];
                } else {
                  // For follow-up questions, check if they should be shown
                  const isFollowUp = followUpQuestionIds.has(item.id);

                  if (isFollowUp) {
                    // Only include follow-up questions that should be shown
                    // We need to explicitly check for true, as undefined or false means don't show
                    console.log(`Follow-up question ${item.id} visibility:`, showFollowUp[item.id]);
                    if (showFollowUp[item.id] === true) {
                      // Make sure follow-up questions have proper title/text formatting
                      // If title and text are the same, only keep one to avoid duplication
                      if (item.title === item.text) {
                        item.description = null; // Prevent duplication in description
                      }
                      
                      // Add question to current category
                      if (currentCategory) {
                        currentCategoryQuestions.push(item);
                      } else {
                        // If no category has been defined yet, create an 'Uncategorized' category
                        if (questionsByCategory.length === 0) {
                          currentCategory = { id: 0, text: 'Uncategorized' };
                          currentCategoryQuestions = [item];
                        } else {
                          // Add to the first category if it exists
                          questionsByCategory[0].questions.push(item);
                        }
                      }
                    }
                  } else {
                    // For regular questions (not follow-ups), always include them
                    // Add question to current category
                    if (currentCategory) {
                      currentCategoryQuestions.push(item);
                    } else {
                      // If no category has been defined yet, create an 'Uncategorized' category
                      if (questionsByCategory.length === 0) {
                        currentCategory = { id: 0, text: 'Uncategorized' };
                        currentCategoryQuestions = [item];
                      } else {
                        // Add to the first category if it exists
                        questionsByCategory[0].questions.push(item);
                      }
                    }
                  }
                }
              });

              // Add the last category if it exists
              if (currentCategory && currentCategoryQuestions.length > 0) {
                questionsByCategory.push({
                  category: currentCategory,
                  questions: currentCategoryQuestions
                });
              }

              // Calculate total questions for progress indicator
              const totalQuestions = questionsByCategory.reduce(
                (total, category) => total + category.questions.length, 0
              );

              // Calculate which question we're on overall
              let currentQuestionNumber = 0;
              for (let i = 0; i < questionsByCategory.length; i++) {
                const category = questionsByCategory[i];
                for (let j = 0; j < category.questions.length; j++) {
                  if (i === activeStep / 1000 && j === activeStep % 1000) {
                    break;
                  }
                  currentQuestionNumber++;
                }
              }

              // Find the active category and question
              const activeCategoryIndex = Math.floor(activeStep / 1000);
              const activeQuestionIndex = activeStep % 1000;

              // Make sure we have valid indices
              if (activeCategoryIndex >= questionsByCategory.length) {
                return <Typography>No questions available</Typography>;
              }

              const activeCategory = questionsByCategory[activeCategoryIndex];
              if (!activeCategory || activeQuestionIndex >= activeCategory.questions.length) {
                return <Typography>No questions available</Typography>;
              }

              const activeQuestion = activeCategory.questions[activeQuestionIndex];

              return (
                <Box>
                  {/* Progress indicator */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                    <Typography variant="subtitle1">
                      Question {currentQuestionNumber + 1} of {totalQuestions}
                    </Typography>
                    <Box sx={{ width: '60%' }}>
                      <Box
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.300',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${(currentQuestionNumber / totalQuestions) * 100}%`,
                            bgcolor: 'primary.main',
                            borderRadius: 4
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Category header */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 3,
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText'
                    }}
                  >
                    <Typography variant="h6">
                      {activeCategory.category.text}
                    </Typography>
                  </Paper>

                  {/* Question */}
                  <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {activeQuestion.title}
                      {activeQuestion.required && <span style={{ color: 'red' }}> *</span>}
                    </Typography>

                    {activeQuestion.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {activeQuestion.description}
                      </Typography>
                    )}
                    
                    {!activeQuestion.description && activeQuestion.text && activeQuestion.text !== activeQuestion.title && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {activeQuestion.text}
                      </Typography>
                    )}

                    {renderQuestionInput(activeQuestion)}

                    {/* Navigation buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                      <Button
                        variant="outlined"
                        onClick={handleBack}
                        startIcon={<BackIcon />}
                        disabled={activeCategoryIndex === 0 && activeQuestionIndex === 0}
                      >
                        Back
                      </Button>

                      <Button
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<NextIcon />}
                      >
                        {(activeCategoryIndex === questionsByCategory.length - 1 &&
                          activeQuestionIndex === activeCategory.questions.length - 1) ? 'Review' : 'Next'}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              );
            })()}

            {/* Review Mode */}
            {isReviewMode && (
              <Box sx={{ p: 3, mt: 2 }}>
                <Typography variant="h5" gutterBottom>
                  Review Your Responses
                </Typography>
                <Typography variant="body1" paragraph>
                  Please review your answers before submitting. You can go back to make changes if needed.
                </Typography>

                {/* Display responses by category */}
                {(() => {
                  // Group questions by category
                  const questionsByCategory = {};
                  const actualQuestions = questions.filter(q => !q.isCategory);

                  // Find the category for each question
                  const getQuestionCategory = (question) => {
                    // Find the category that precedes this question
                    for (let i = questions.indexOf(question) - 1; i >= 0; i--) {
                      if (questions[i].isCategory) {
                        return {
                          id: questions[i].id,
                          text: questions[i].text
                        };
                      }
                    }
                    return { id: 0, text: 'Uncategorized' };
                  };

                  // Group questions by category
                  actualQuestions.forEach(question => {
                    // Identify follow-up questions
                    const isFollowUp = questions.some(q => {
                      if (q.has_follow_up && q.follow_up_question_id && parseInt(q.follow_up_question_id) === question.id) {
                        return true;
                      }
                      if (q.has_follow_up && Array.isArray(q.follow_up_rules)) {
                        return q.follow_up_rules.some(rule => {
                          const followUpId = rule.question_id || rule.follow_up_question_id;
                          return followUpId && parseInt(followUpId) === question.id;
                        });
                      }
                      return false;
                    });

                    // Skip follow-up questions that should not be shown
                    // Only skip if it's a follow-up AND showFollowUp is explicitly false
                    if (isFollowUp && showFollowUp[question.id] === false) {
                      console.log('Skipping hidden follow-up question in review:', question.id);
                      return null;
                    }

                    const category = getQuestionCategory(question);
                    if (!questionsByCategory[category.id]) {
                      questionsByCategory[category.id] = {
                        name: category.text,
                        questions: []
                      };
                    }
                    questionsByCategory[category.id].questions.push(question);
                  });

                  return Object.values(questionsByCategory).map((category, categoryIndex) => (
                    <Paper key={categoryIndex} variant="outlined" sx={{ p: 2, mb: 3 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        {category.name}
                      </Typography>

                      {category.questions.map((question, questionIndex) => {
                        // Format the response based on question type
                        let formattedResponse = '';
                        const response = responses[question.id];

                        if (Array.isArray(response)) {
                          // For checkboxes
                          formattedResponse = response.join(', ');
                        } else if (question.type === 'radio' || question.type === 'select' || question.type === 'dropdown') {
                          // For radio buttons and dropdowns, show the selected option
                          const selectedOption = question.options?.find(opt =>
                            (opt.value || opt) === response
                          );
                          formattedResponse = selectedOption ? (selectedOption.label || selectedOption) : response;
                        } else {
                          // For text inputs
                          formattedResponse = response || 'No response';
                        }

                        // Get uploaded files for this question
                        const files = uploadedFiles[question.id] || [];

                        return (
                          <Box key={questionIndex} sx={{ mb: 2, pl: 2, borderLeft: '2px solid #eee' }}>
                            <Typography variant="subtitle1" gutterBottom>
                              {question.title}
                              {question.required && <span style={{ color: 'red' }}> *</span>}
                            </Typography>

                            {question.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {question.description}
                              </Typography>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  Your Answer:
                                </Typography>
                                <Typography variant="body1">
                                  {formattedResponse}
                                </Typography>

                                {files.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                      Uploaded Files:
                                    </Typography>
                                    <List dense>
                                      {files.map((file, fileIndex) => (
                                        <ListItem key={fileIndex}>
                                          <ListItemIcon>
                                            <AttachFileIcon fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText primary={file.name} />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </Box>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setIsReviewMode(false);
                                  // Find the index of this question in the actualQuestions array
                                  const actualQuestions = questions.filter(q => !q.isCategory);
                                  const questionIndex = actualQuestions.findIndex(q => q.id === question.id);
                                  if (questionIndex !== -1) {
                                    setActiveStep(questionIndex);
                                  }
                                }}
                              >
                                Edit
                              </Button>
                            </Box>
                          </Box>
                        );
                      })}
                    </Paper>
                  ));
                })()}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    startIcon={<BackIcon />}
                  >
                    Back to Questions
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    startIcon={<SendIcon />}
                  >
                    Submit Questionnaire
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientQuestionnaire;
